import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowLeft, Building2, Home, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type { IBuilding, IFlat } from "../types/quarters.types";

const QuartersInventory = () => {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [buildings, setBuildings] = useState<IBuilding[]>([]);
  const [flats, setFlats] = useState<IFlat[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    buildingId: "",
    status: "",
    quarterType: "",
    q: "",
    page: 1,
    limit: 20,
  });
  const [totalPages, setTotalPages] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [buildingsRes, flatsRes] = await Promise.all([
        quartersService.getBuildings(),
        quartersService.getFlats({
          buildingId: filters.buildingId || undefined,
          status: filters.status || undefined,
          quarterType: filters.quarterType || undefined,
          q: filters.q || undefined,
          page: filters.page,
          limit: filters.limit,
        }),
      ]);

      if (buildingsRes.success) setBuildings(buildingsRes.data);
      if (flatsRes.success) {
        setFlats(flatsRes.data);
        setTotalPages(flatsRes.totalPages || 1);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load quarters inventory");
    } finally {
      setLoading(false);
    }
  }, [filters, quartersService, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    loadData();
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      <div className="mb-8">
        <button
          onClick={() => navigate("/quarters")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Dashboard
        </button>
        <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
          <Building2 className="w-8 h-8 text-blue-600" /> Quarters & Vacancy
        </h1>
        <p className="text-slate-500 text-sm mt-1">Browse buildings, flat types, and current availability.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {buildings.map((building) => (
          <div key={building._id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start gap-2">
                <h3 className="font-bold text-slate-800">{building.name}</h3>
                {building.quarterType && (
                  <span className="bg-blue-50 text-blue-700 text-[10px] font-extrabold px-2 py-0.5 rounded-md uppercase">
                    {building.quarterType}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Code: {building.code}</p>
              <p className="text-xs text-slate-500">{building.campus || "Main Campus"}</p>
              <div className="flex gap-4 mt-4 text-sm">
                <span className="text-slate-600">Floors: <strong>{building.totalFloors || 0}</strong></span>
                <span className="text-slate-600">Flats: <strong>{building.totalFlats || 0}</strong></span>
              </div>
            </div>
            {building.quarterType && (
              <button
                onClick={() => navigate("/quarters/apply", { state: { quarterType: building.quarterType } })}
                className="mt-4 w-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold py-2 rounded-xl transition flex items-center justify-center gap-1"
              >
                Apply for {building.quarterType}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm mb-6">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Building</label>
            <select
              value={filters.buildingId}
              onChange={(e) => setFilters((prev) => ({ ...prev, buildingId: e.target.value, page: 1 }))}
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2 text-sm"
            >
              <option value="">All Buildings</option>
              {buildings.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))}
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="vacant">Vacant</option>
              <option value="occupied">Occupied</option>
              <option value="reserved">Reserved</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Quarter Type</label>
            <input
              value={filters.quarterType}
              onChange={(e) => setFilters((prev) => ({ ...prev, quarterType: e.target.value }))}
              placeholder="e.g. Type II"
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Search</label>
            <input
              value={filters.q}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
              placeholder="Flat number..."
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-4 py-2.5 rounded-xl flex items-center justify-center gap-2"
          >
            <Search className="w-4 h-4" /> Search
          </button>
        </form>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-500">Loading inventory...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Flat</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Building</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Type</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Status</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {flats.map((flat) => {
                const bType = typeof flat.buildingId === "object" ? (flat.buildingId as IBuilding).quarterType : "";
                return (
                  <tr key={flat._id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium">{flat.flatNumber}</td>
                    <td className="px-4 py-3">
                      {typeof flat.buildingId === "object"
                        ? (flat.buildingId as IBuilding).name
                        : "-"}
                    </td>
                    <td className="px-4 py-3">
                      {bType ? (
                        <span className="bg-slate-100 text-slate-800 text-xs font-bold px-2 py-0.5 rounded">
                          {bType}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                        flat.status === "vacant" ? "bg-emerald-100 text-emerald-800" :
                        flat.status === "occupied" ? "bg-indigo-100 text-indigo-800" :
                        "bg-slate-100 text-slate-700"
                      }`}>
                        {flat.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => navigate(`/quarters/flat/${flat._id}`)}
                          className="text-blue-600 hover:text-blue-800 font-bold text-xs flex items-center gap-1"
                        >
                          <Home className="w-3.5 h-3.5" /> View Details
                        </button>
                        {flat.status === "vacant" && bType && (
                          <button
                            onClick={() => navigate("/quarters/apply", { state: { quarterType: bType } })}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-3 py-1 rounded-lg transition"
                          >
                            Apply Now
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            disabled={filters.page <= 1}
            onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-slate-600">Page {filters.page} of {totalPages}</span>
          <button
            disabled={filters.page >= totalPages}
            onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default QuartersInventory;
